import isEqual from 'lodash/isEqual';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import React, { PropsWithChildren, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import {
  CheckoutNoResults,
  CheckoutNoResultsModal,
  CheckoutResults,
  CheckoutResultsModal,
  CheckoutWaitingScreen,
  CheckoutWaitingScreenModal,
} from '@hotelplan/components.common.checkout';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { useObjectId } from '@hotelplan/libs.context.object-id';
import { useSearchState } from '@hotelplan/libs.search-state';
import { sx2CssThemeFn } from '@hotelplan/libs.sx';
import {
  trackORLeCommerce,
  ECommerceEventType,
  trackORLeCommerceNoResults,
} from '@hotelplan/libs.track.orl';
import { trackORLResult } from '@hotelplan/libs.tracking';
import { SingleOrlItem, TravelType } from '@hotelplan/platform-graphql-api';
import usePageFooter from 'components/domain/footer/usePageFooter';
import SrlPdpLastSeenHotels from 'components/domain/last-seen-hotels/SrlPdpLastSeenHotels';
import { useLastSeenHotels } from 'components/domain/last-seen-hotels/useLastSeenHotels';
import { normalizeCheckoutComponent } from 'components/domain/orl/Checkout.mappers';
import {
  mapORLSingleResultsToTrackableResultsEventMetaData,
  mapORLStateToTrackableData,
  mapSearchControlRoomsToOfferRooms,
} from 'components/domain/orl/Orl.mappers';
import { IOrlState } from 'components/domain/orl/Orl.types';
import OrlResults from 'components/domain/orl/orlResults/OrlResults';
import { ORLSortingDropdown } from 'components/domain/orl/orlResults/ORLSorting';
import OrlResultsSkeleton from 'components/domain/orl/skeletons/OrlResultsSkeleton.skeleton';
import { ORLCheckoutState } from 'components/domain/orl/useORLCheckout';
import PaymentMethods from 'components/domain/payment-methods/PaymentMethods';
import { SRLNoResults } from 'components/domain/srl/SRLEmptyResults';
import { Bd4recommendationFragment } from 'graphql/components/BD4RecommendationGroup.generated';
import { useGetOrlCheckoutWaitingScreenQuery } from 'graphql/orl/GetORLCheckoutWaitingScreen.generated';
import { useGetPdpPageContentQuery } from 'graphql/pdp/GetPdpPageContent.generated';
import { mapTravelTypeToOfferTravelType } from 'libs';
import { usePdpScrollContext } from './PdpScrollContext';
import usePdpOffers from './usePdpOffers';

const OrlWrap = styled.div(
  sx2CssThemeFn({
    display: [null, 'grid'],
    gridTemplateColumns: [null, '250px 1fr'],
    gap: [null, '16px'],
    '.orl-header': {
      gridColumn: [null, 'span 2'],
    },
    '.mobile-children': {
      borderTop: '1px solid',
      borderColor: 'borderGray',
      pt: 3,
      my: 3,
      display: 'flex',
      flexWrap: 'wrap',
      bg: 'backgroundGray',
      '.all-offers-title': {
        color: 'primary',
        mt: 2,
        ml: 2,
        fontSize: 1,
      },
    },
  })
);

const OrlHeader = styled.header(
  sx2CssThemeFn({
    gridColumn: [null, 'span 2'],
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: [0, '14px 12px 20px 30px'],
    mb: [2, 0],
    '.title': {
      fontSize: [1, '26px'],
      color: 'secondary',
    },
    '.subtitle': {
      fontSize: [1, '22px'],
      color: '#999',
      ml: 2,
    },
    '.travelers-subtitle': {
      color: '#999',
      fontSize: [1, '22px'],
    },
  })
);

const HorizontalBd4TravelRecommendation = dynamic(
  () =>
    import('components/domain/bd4').then(
      m => m.HorizontalBd4TravelRecommendation
    ),
  { ssr: false }
);

interface IPdpOrlContainerProps {
  productName?: string;
  orlCheckout: ORLCheckoutState;
  bd4Group?: { name: string; items: Array<Bd4recommendationFragment> } | null;
  bd4GroupLoading?: boolean;
}

const PdpOrlContainer = ({
  productName,
  children,
  orlCheckout,
  bd4Group,
  bd4GroupLoading,
}: PropsWithChildren<IPdpOrlContainerProps>) => {
  const [t, { language }] = useTranslation(['orl', 'common']);
  const { mobile } = useDeviceType();
  const {
    state: { searchControl },
  } = useSearchState<IOrlState>();
  const searchControlRef = useRef(null);

  const { data: waitingScreenData } = useGetOrlCheckoutWaitingScreenQuery({
    ssr: false,
  });
  const lastSeenHotels = useLastSeenHotels();

  const { id } = useObjectId();

  const { data: pdpContent } = useGetPdpPageContentQuery({
    variables: { id },
  });

  const breadCrumbItems =
    pdpContent?.pdp.content.overview?.productBreadcrumbs.breadcrumbs.map(
      item => ({
        label: item.caption,
      })
    ) || [];

  const {
    onCheckout,
    isNoResultsModalShown,
    closeCheckoutResults,
    areCheckoutResultsShown,
    closeNoResultsModal,
    data,
    waitingScreenImg,
    isForceClosed,
    forceClose,
    checkoutLoading,
  } = orlCheckout;

  const { refValue } = usePdpScrollContext();
  const { loading, offers, possibleNextPage, onShowMore, totalResults } =
    usePdpOffers(50, (res, queryTime, orlState) => {
      trackORLResult(
        mapORLSingleResultsToTrackableResultsEventMetaData(res, queryTime),
        mapORLStateToTrackableData(orlState)
      );
    });

  useEffect(() => {
    const offer = offers?.[0];
    if (
      offers?.length &&
      offer &&
      !isEqual(searchControlRef?.current, searchControl)
    ) {
      trackORLeCommerce({
        offer: offer as SingleOrlItem,
        id,
        language,
        productName,
        eventType: ECommerceEventType.VIEW_ITEM,
      });
      searchControlRef.current = searchControl;
    }

    if (
      offers?.length === 0 &&
      !isEqual(searchControlRef?.current, searchControl)
    ) {
      trackORLeCommerceNoResults({
        id,
        productName,
        language,
        searchControlTravelType: searchControl.travelType,
        rooms: searchControl.travelRooms,
      });
      searchControlRef.current = searchControl;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offers]);

  const travelersCount = useMemo(() => {
    return mapSearchControlRoomsToOfferRooms(searchControl?.travelRooms);
  }, [searchControl?.travelRooms]);

  const { paymentMethods } = usePageFooter();
  return (
    <OrlWrap ref={refValue}>
      {mobile ? (
        <div className="mobile-children">
          <h3 className="all-offers-title">
            {t('common:allOffers', {
              count: totalResults,
            })}{' '}
            ({mapTravelTypeToOfferTravelType(searchControl?.travelType, t)})
          </h3>{' '}
          {children}
        </div>
      ) : null}
      <OrlHeader>
        <div>
          <h2 className="title">
            {!mobile && productName ? (
              <>
                {productName}
                <span className="subtitle">
                  {t(
                    searchControl?.travelType === TravelType.Package
                      ? 'orl:package.title'
                      : 'orl:hotel.title'
                  )}{' '}
                  {t('common:adult.count', { count: travelersCount.adults })}
                  {travelersCount.children > 0 &&
                    `, ${t('common:child.count', {
                      count: travelersCount.children,
                    })}`}
                </span>
              </>
            ) : null}
          </h2>
        </div>
        <ORLSortingDropdown travelType={searchControl?.travelType} />
      </OrlHeader>
      {!mobile ? <div>{children}</div> : null}
      <div>
        {loading ? (
          <OrlResultsSkeleton />
        ) : (
          <>
            {!offers?.length ? (
              <SRLNoResults />
            ) : (
              <OrlResults
                offers={offers}
                onShowMore={onShowMore}
                possibleNextPage={possibleNextPage}
                onCheckout={onCheckout}
              />
            )}
          </>
        )}
        <CheckoutNoResultsModal
          show={isNoResultsModalShown}
          onClose={closeNoResultsModal}
        >
          <CheckoutNoResults onCancel={closeNoResultsModal} />
        </CheckoutNoResultsModal>
        <CheckoutResultsModal
          show={areCheckoutResultsShown}
          onClose={closeCheckoutResults}
        >
          {data && (
            <CheckoutResults
              {...normalizeCheckoutComponent(data.orl.checkout)}
              onCancel={closeCheckoutResults}
              onAccept={() => {
                closeCheckoutResults();

                if (data.orl.checkout.link) {
                  window.location.href = data.orl.checkout.link.url;
                }
              }}
            />
          )}
        </CheckoutResultsModal>
        <CheckoutWaitingScreenModal
          show={!isForceClosed && checkoutLoading}
          onClose={forceClose}
        >
          <CheckoutWaitingScreen
            title={waitingScreenData?.productWaitingScreen.title || ''}
            subTitle={waitingScreenData?.productWaitingScreen.subtitle || ''}
            text={waitingScreenData?.productWaitingScreen.text || ''}
            image={{
              src: waitingScreenImg,
              resized: [],
              alt: 'Checkout waiting screen',
            }}
            productName={pdpContent?.pdp.content.overview.productName}
            breadCrumbs={breadCrumbItems}
            paymentMethods={<PaymentMethods items={paymentMethods} />}
          />
        </CheckoutWaitingScreenModal>
      </div>
      {mobile ? (
        <>
          {lastSeenHotels?.length ? (
            <SrlPdpLastSeenHotels lastSeenHotels={lastSeenHotels} />
          ) : null}
          <HorizontalBd4TravelRecommendation
            name={bd4Group?.name}
            items={bd4Group?.items}
            loading={bd4GroupLoading}
          />
        </>
      ) : null}
    </OrlWrap>
  );
};

export default PdpOrlContainer;
